export enum LogLevel {
  TRACE = 0,
  DEBUG = 1,
  INFO = 2,
  WARN = 3,
  ERROR = 4,
  NONE = 999, // Disable all logging
}

export interface LogConfig {
  level: LogLevel;
  enableTimestamp: boolean;
  enableContext: boolean;
  prefix?: string;
}

export class Logger {
  private config: LogConfig;

  constructor(config: Partial<LogConfig> = {}) {
    this.config = {
      level: this.getEnvironmentLogLevel(),
      enableTimestamp: true,
      enableContext: true,
      ...config,
    };
  }

  /**
   * Determine log level based on environment variables and build mode
   */
  private getEnvironmentLogLevel(): LogLevel {
    // Check for explicit LOG_LEVEL environment variable first
    const envLogLevel = import.meta.env.VITE_LOG_LEVEL;
    if (envLogLevel) {
      // Support 'NONE' as a string value
      if (envLogLevel.toUpperCase() === 'NONE') {
        return LogLevel.NONE;
      }
      const level = LogLevel[envLogLevel.toUpperCase() as keyof typeof LogLevel];
      if (level !== undefined) {
        return level;
      }
    }

    // Fallback to mode-based configuration
    const mode = import.meta.env.MODE;
    switch (mode) {
      case 'development':
        return LogLevel.DEBUG;
      case 'staging':
      case 'uat':
        return LogLevel.INFO;
      case 'production':
        return LogLevel.WARN;
      default:
        return LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private formatMessage(level: LogLevel, message: string, context?: string): string {
    const parts: string[] = [];

    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    if (this.config.enableTimestamp) {
      parts.push(new Date().toISOString());
    }

    parts.push(`[${LogLevel[level]}]`);

    if (context && this.config.enableContext) {
      parts.push(`[${context}]`);
    }

    parts.push(message);

    return parts.join(' ');
  }

  private log(level: LogLevel, message: string, data?: any, context?: string): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);

    switch (level) {
      case LogLevel.TRACE:
      case LogLevel.DEBUG:
        if (data !== undefined) {
          console.debug(formattedMessage, data);
        } else {
          console.debug(formattedMessage);
        }
        break;
      case LogLevel.INFO:
        if (data !== undefined) {
          console.info(formattedMessage, data);
        } else {
          console.info(formattedMessage);
        }
        break;
      case LogLevel.WARN:
        if (data !== undefined) {
          console.warn(formattedMessage, data);
        } else {
          console.warn(formattedMessage);
        }
        break;
      case LogLevel.ERROR:
        if (data !== undefined) {
          console.error(formattedMessage, data);
        } else {
          console.error(formattedMessage);
        }
        break;
    }
  }

  /**
   * Log trace level messages (most verbose)
   * Used for detailed debugging information, function entry/exit
   */
  trace(message: string, data?: any, context?: string): void {
    this.log(LogLevel.TRACE, message, data, context);
  }

  /**
   * Log debug level messages
   * Used for debugging information, development diagnostics
   */
  debug(message: string, data?: any, context?: string): void {
    this.log(LogLevel.DEBUG, message, data, context);
  }

  /**
   * Log info level messages
   * Used for general information, application flow
   */
  info(message: string, data?: any, context?: string): void {
    this.log(LogLevel.INFO, message, data, context);
  }

  /**
   * Log warning level messages
   * Used for warnings that don't prevent operation
   */
  warn(message: string, data?: any, context?: string): void {
    this.log(LogLevel.WARN, message, data, context);
  }

  /**
   * Log error level messages
   * Used for errors and exceptions
   */
  error(message: string, data?: any, context?: string): void {
    this.log(LogLevel.ERROR, message, data, context);
  }

  /**
   * Set the minimum log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Get the current log level
   */
  getLevel(): LogLevel {
    return this.config.level;
  }

  /**
   * Get current environment mode
   */
  getMode(): string {
    return import.meta.env.MODE || 'development';
  }

  /**
   * Check if a specific log level is enabled
   */
  isLevelEnabled(level: LogLevel): boolean {
    return this.shouldLog(level);
  }

  /**
   * Create a child logger with context
   */
  child(context: string): Logger {
    return new Logger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}:${context}` : context,
    });
  }

  /**
   * Log with custom level (useful for migration)
   */
  logWithLevel(level: LogLevel, message: string, data?: any, context?: string): void {
    this.log(level, message, data, context);
  }
}

// Create and export the default logger instance
export const logger = new Logger();

// Export logger factory for custom configurations
export const createLogger = (config?: Partial<LogConfig>): Logger => {
  return new Logger(config);
};

// Export LogLevel for external use
export { LogLevel as Level };

// Utility function to get environment info
export const getLoggerInfo = () => ({
  level: LogLevel[logger.getLevel()],
  mode: logger.getMode(),
  isDebugEnabled: logger.isLevelEnabled(LogLevel.DEBUG),
  isTraceEnabled: logger.isLevelEnabled(LogLevel.TRACE),
});
